import { OptionButton } from "@/components/landing/ui";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const FURNITURE = ["Dining Table", "Dining Set", "Dining Chairs", "Dresser", "Sideboard", "Coffee Table", "Cabinet / Hutch",
  "Desk", "Bed Frame", "Armoire", "Sidetable", "Chest", "Other"];
export const COLOUR_GOALS = [
  ["love_current", "I love the current colour — it just looks worn"],
  ["same", "I want approximately the same colour"],
  ["change", "I want to change the colour significantly"],
  ["unsure", "I'm not sure yet"],
];
export const CONCERNS = ["Scratches", "Dull finish", "Scuffs / wear", "Water marks", "Fading", "General aging", "Damage / repairs needed", "Other"];
export const TIMELINES = [
  ["asap", "As soon as possible"],
  ["month", "Within the next month"],
  ["1_3_months", "Within 1–3 months"],
  ["exploring", "Just exploring my options"],
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");
const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export const StepHeading = ({ title, sub }) => (
  <div className="mb-7">
    <h3 className="font-display text-2xl sm:text-3xl tracking-tight">{title}</h3>
    {sub && <p className="text-gray-500 text-sm mt-2 leading-relaxed">{sub}</p>}
  </div>
);

export const FurnitureStep = ({ f, set }) => (
  <div>
    <StepHeading title="What would you like revived?" sub="Choose everything that applies." />
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {FURNITURE.map((t) => (
        <OptionButton key={t} testId={`furniture-${slug(t)}`} selected={f.furniture_types.includes(t)} onClick={() => set({ furniture_types: toggleIn(f.furniture_types, t) })}>
          {t}
        </OptionButton>
      ))}
    </div>
    {f.furniture_types.includes("Other") && (
      <Input data-testid="furniture-other" placeholder="Tell us what it is" value={f.furniture_other} onChange={(e) => set({ furniture_other: e.target.value })} className="rounded-none h-12 mt-4 text-base" />
    )}
  </div>
);

export const ColourStep = ({ f, set }) => (
  <div>
    <StepHeading title="What do you want to do with the colour?" sub="This helps us tell whether Revival or a full restoration is the better fit." />
    <div className="grid gap-2.5">
      {COLOUR_GOALS.map(([v, label]) => (
        <OptionButton key={v} testId={`colour-${v}`} selected={f.colour_goal === v} onClick={() => set({ colour_goal: v })}>{label}</OptionButton>
      ))}
    </div>
  </div>
);

export const ConcernsStep = ({ f, set }) => (
  <div>
    <StepHeading title="What's bothering you most?" sub="Select all that apply." />
    <div className="grid grid-cols-2 gap-2.5">
      {CONCERNS.map((c) => (
        <OptionButton key={c} testId={`concern-${slug(c)}`} selected={f.concerns.includes(c)} onClick={() => set({ concerns: toggleIn(f.concerns, c) })}>{c}</OptionButton>
      ))}
    </div>
    {f.concerns.includes("Other") && (
      <Input data-testid="concerns-other" placeholder="Tell us more" value={f.concerns_other} onChange={(e) => set({ concerns_other: e.target.value })} className="rounded-none h-12 mt-4 text-base" />
    )}
    <h4 className="font-display text-xl sm:text-2xl tracking-tight mt-10 mb-5">When are you hoping to have this done?</h4>
    <div className="grid sm:grid-cols-2 gap-2.5">
      {TIMELINES.map(([v, label]) => (
        <OptionButton key={v} testId={`timeline-${v}`} selected={f.timeline === v} onClick={() => set({ timeline: v })}>{label}</OptionButton>
      ))}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.16em] text-gray-500 mb-1.5">{label}</span>
    {children}
  </label>
);

export const ContactStep = ({ f, set }) => {
  const inp = "rounded-none h-12 text-base";
  return (
    <div>
      <StepHeading title="Where should we send your assessment?" sub="We'll review your photos and reach out about your furniture." />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="First name"><Input data-testid="contact-first-name" autoComplete="given-name" value={f.first_name} onChange={(e) => set({ first_name: e.target.value })} className={inp} /></Field>
        <Field label="Last name"><Input data-testid="contact-last-name" autoComplete="family-name" value={f.last_name} onChange={(e) => set({ last_name: e.target.value })} className={inp} /></Field>
        <Field label="Email"><Input data-testid="contact-email" type="email" inputMode="email" autoComplete="email" value={f.email} onChange={(e) => set({ email: e.target.value })} className={inp} /></Field>
        <Field label="Phone"><Input data-testid="contact-phone" type="tel" inputMode="tel" autoComplete="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} className={inp} /></Field>
        <Field label="Postal code"><Input data-testid="contact-postal" autoComplete="postal-code" placeholder="L1N 2A3" value={f.postal_code} onChange={(e) => set({ postal_code: e.target.value.toUpperCase() })} className={inp} /></Field>
      </div>
      <div className="mt-4">
        <Field label="Project notes (optional)">
          <Textarea data-testid="contact-notes" rows={3} placeholder="Anything else we should know about the piece?" value={f.notes} onChange={(e) => set({ notes: e.target.value })} className="rounded-none text-base" />
        </Field>
      </div>
    </div>
  );
};
